import { computed, reactive, ref } from 'vue'
import { githubApi } from '@/api/github'
import { createRequestGuard, withAsyncState } from '@/lib/asyncState'
import type { PaginationFilters, PaginationMeta, PendingRepoRule } from '@/types/models'

// Singleton, following useRepoScans.ts's shape: shared list/pagination/
// selection state so the view retains its place across navigation within a
// session.
const rules = ref<PendingRepoRule[]>([])
const meta = ref<PaginationMeta | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
const bulkActionPending = ref(false)

// A Set (not an array) so toggling one row's checkbox is O(1) regardless of
// how many others are already selected.
const selectedIds = reactive(new Set<string>())

// Guards against a stale filter/page request's response landing after a
// newer one's and clobbering it.
const requestGuard = createRequestGuard()

export function usePendingRepoRules() {
  async function fetchPendingRules(filters: PaginationFilters = {}): Promise<void> {
    const token = requestGuard.start()
    await withAsyncState(
      error,
      'Failed to load pending-review rules.',
      async () => {
        const page = await githubApi.listPendingRepoRules(filters)
        if (!requestGuard.isCurrent(token)) return
        rules.value = page.data
        meta.value = page.meta
        // Rows dropped by the reload (already resolved by someone/something
        // else) shouldn't linger as phantom selections.
        const liveIds = new Set(page.data.map((rule) => rule.id))
        for (const id of selectedIds) {
          if (!liveIds.has(id)) selectedIds.delete(id)
        }
      },
      loading,
      undefined,
      () => !requestGuard.isCurrent(token),
    )
  }

  function isSelected(id: string): boolean {
    return selectedIds.has(id)
  }

  function toggleSelected(id: string): void {
    if (selectedIds.has(id)) selectedIds.delete(id)
    else selectedIds.add(id)
  }

  function selectAll(): void {
    rules.value.forEach((rule) => selectedIds.add(rule.id))
  }

  function clearSelection(): void {
    selectedIds.clear()
  }

  // Removes the acted-on rows locally rather than refetching, so a bulk
  // action doesn't reset the user back to a query they'd have to re-run —
  // and it can't silently resurrect a row that another tab already resolved,
  // since we only ever remove, never re-add, from what the server told us.
  function removeFromList(ids: string[]): void {
    const removed = new Set(ids)
    rules.value = rules.value.filter((rule) => !removed.has(rule.id))
    if (meta.value) meta.value = { ...meta.value, total: Math.max(0, meta.value.total - removed.size) }
  }

  async function resolveRules(
    ids: string[],
    apiCall: (ids: string[]) => Promise<unknown>,
    fallbackMessage: string,
  ): Promise<boolean> {
    if (ids.length === 0) return false
    const result = await withAsyncState(
      error,
      fallbackMessage,
      async () => {
        await apiCall(ids)
        removeFromList(ids)
        ids.forEach((id) => selectedIds.delete(id))
        if (rules.value.length === 0 && meta.value && meta.value.total > 0) {
          const targetPage = meta.value.current_page > 1 ? meta.value.current_page - 1 : 1
          await fetchPendingRules({ page: targetPage })
        }
        return true
      },
      bulkActionPending,
    )
    return result === true
  }

  function bulkApprove(ids: string[]): Promise<boolean> {
    return resolveRules(ids, (ids) => githubApi.bulkApproveRepoRules(ids), 'Failed to approve the selected rules.')
  }

  function bulkReject(ids: string[]): Promise<boolean> {
    return resolveRules(ids, (ids) => githubApi.bulkRejectRepoRules(ids), 'Failed to reject the selected rules.')
  }

  // Called on logout so the next session's pending-rules view doesn't briefly
  // show the previous user's data before its own fetch resolves, and so a
  // late response from a request the previous session started can't land and
  // repopulate it.
  function reset(): void {
    requestGuard.start()
    rules.value = []
    meta.value = null
    loading.value = false
    error.value = null
    bulkActionPending.value = false
    selectedIds.clear()
  }

  return {
    rules: computed(() => rules.value),
    meta,
    loading,
    error,
    bulkActionPending,
    selectedIds: computed(() => selectedIds),
    fetchPendingRules,
    isSelected,
    toggleSelected,
    selectAll,
    clearSelection,
    bulkApprove,
    bulkReject,
    reset,
  }
}
